import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router";
import { convertKeysDeepToLowerCamel } from "utils/helper/convertNamingConvention";
import { usePostSse } from "hooks/useRequest";
import { Chat, Reference } from "models/AiResearch";
import { getNow } from "utils/helper/day";
import { useToast } from "hooks/useToast";

export const useQuery = ({
  result,
  setResult,
  fetchHistoryList
}: {
  result: Chat;
  setResult: React.Dispatch<React.SetStateAction<Chat>>;
  fetchHistoryList: () => void;
}) => {
  const { pathname, search } = useLocation();
  const searchRef = useRef(search);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  const pathnameRef = useRef(pathname);
  const toast = useToast();
  // SSEステータスのデフォルト値がclosedのため、一番最初は履歴を取得しないようにフラグを用意
  const shouldFetchHistoryOnCloseRef = useRef(false);

  useEffect(() => {
    pathnameRef.current = pathname;
    searchRef.current = search;
    navigateRef.current = navigate;
  }, [navigate, pathname, search]);

  // エラーハンドリングドキュメント
  // https://docs.google.com/spreadsheets/d/1MNs7QLYV_0zdlFIClr6xG0BpzeQsm2sZfIU_G215CcA/edit?gid=1738421261#gid=1738421261
  const handleSseEvent = useCallback(
    (e: { event?: string; data: string | object }) => {
      // メッセージ内容
      switch (e.event) {
        // 回答内容
        case "message": {
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            newResult.history[lastIndex].answer += e.data;
            newResult.history[lastIndex].isTextGenerating = true;
            return newResult;
          });
          break;
        }
        // 質問最適化内容
        case "question_assistance": {
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            const answer =
              newResult.history[lastIndex].questionOptimization || "";
            newResult.history[lastIndex].questionOptimization = answer + e.data;
            return newResult;
          });
          break;
        }
        // 進捗状況
        case "progress": {
          if (typeof e.data === "string") return;
          const convertedData = convertKeysDeepToLowerCamel(e.data) as any;
          if (convertedData.nameJa) {
            setResult(prev => {
              const newResult = { ...prev };
              const lastIndex = newResult.history.length - 1;
              if (lastIndex < 0) return newResult;
              newResult.history[
                lastIndex
              ].progress = `${convertedData.nameJa}...`;
              return newResult;
            });
          }
          break;
        }
        // 関連質問
        case "related_questions": {
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            if (lastIndex < 0) return newResult;
            newResult.history[lastIndex].relatedQuestions = (e.data as {
              questions: string[];
            }).questions;
            return newResult;
          });
          return;
        }
        // トークID
        case "vs_talk_id": {
          const talkId = (e.data as string).replace(" ", "");
          setResult(prev => {
            const newResult = { ...prev };
            newResult.talkId = talkId;
            return newResult;
          });

          const searchParams = new URLSearchParams(searchRef.current);
          if (searchParams.get("talkId") !== talkId) {
            searchParams.set("talkId", talkId);
            navigateRef.current(
              `${pathnameRef.current}?${searchParams.toString()}`,
              {
                replace: true
              }
            );
          }
          break;
        }
        // ソース情報
        case "citation_source": {
          if (typeof e.data === "string") return;
          const convertedData = convertKeysDeepToLowerCamel(e.data);

          const dataType: string = (convertedData as any).dataType;

          if (
            dataType !== "article_chunk" &&
            dataType !== "financial_chart" &&
            dataType !== "macro_ir_chart" &&
            dataType !== "macro_chart" &&
            dataType !== "financial_table" &&
            dataType !== "company_list_table"
          ) {
            return;
          }

          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            newResult.history[lastIndex].reference?.push(
              convertedData as Reference
            );
            return newResult;
          });

          break;
        }
        // ID情報設定
        case "ids": {
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            if (lastIndex < 0 || typeof e.data !== "object") return newResult;
            const convertedData = convertKeysDeepToLowerCamel(e.data);
            newResult.history[lastIndex].ids = convertedData as {
              questionId: string;
              answerId: string;
            };
            return newResult;
          });
          return;
        }
        // エラー発生時
        case "error": {
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            if (lastIndex < 0) return newResult;
            newResult.history[lastIndex].isError = true;
            newResult.history[lastIndex].answer = (e.data as any).user_message;
            return newResult;
          });
          break;
        }
        // エラー発生時
        case "vs_sse_api_error": {
          const code = (e.data as any).code;
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            if (lastIndex < 0) return newResult;
            newResult.history[lastIndex].isError = true;
            newResult.history[lastIndex].visibleRetryButton = true;
            newResult.history[lastIndex].errorCode = code;

            if (code === 429) {
              newResult.history[lastIndex].answer =
                "システムエラーが発生しました。\n一時的な負荷や通信状況が原因の可能性があります。\nしばらく時間をおいてから、もう一度お試しください。";
            } else {
              newResult.history[lastIndex].answer =
                "回答の生成中にエラーが発生しました。\nしばらく時間をおいてから再度お試しください。";
            }

            return newResult;
          });
          return;
        }
        // 接続エラー
        case "vs_connection_error": {
          const code = (e.data as any).code;

          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            if (lastIndex < 0) return newResult;
            newResult.history[lastIndex].isError = true;
            newResult.history[lastIndex].answer =
              "回答の生成中にエラーが発生しました。\nしばらく時間をおいてから再度お試しください。";
            newResult.history[lastIndex].errorCode = code;
            newResult.history[lastIndex].visibleRetryButton = true;

            return newResult;
          });
          return;
        }
        case "vs_post_error": {
          setResult(prev => {
            const newResult = { ...prev };
            const lastIndex = newResult.history.length - 1;
            if (lastIndex < 0) return newResult;
            newResult.history[lastIndex].isError = true;
            newResult.history[lastIndex].answer =
              "回答内容の保存に失敗しました。\nしばらく時間をおいてから再度お試しください。";
            return newResult;
          });
          return;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { status, trigger, close } = usePostSse<{
    question: string;
    history: { question: string; answer: string }[];
    talkId?: string;
  }>("/query/rag", {
    onEvent: handleSseEvent,
    onStatusChange: (newStatus, prevStatus, meta) => {
      setResult(prev => {
        const newResult = { ...prev };
        const lastIndex = newResult.history.length - 1;
        if (lastIndex < 0) return newResult;
        newResult.history[lastIndex].sseStatus = newStatus;
        return newResult;
      });

      // SSEが途中で閉じたときのエラーハンドリング内容
      // https://docs.google.com/spreadsheets/d/1MNs7QLYV_0zdlFIClr6xG0BpzeQsm2sZfIU_G215CcA/edit?gid=1738421261#gid=1738421261&range=44:44
      if (
        newStatus === "error" &&
        prevStatus === "open" &&
        meta === undefined
      ) {
        toast.setErrors(
          "システムエラーが発生しました。\nしばらく時間をおいてから再度お試しください。"
        );
      }

      // SSEが閉じたら、過去のチャット履歴を再取得して最新の状態にする
      if (newStatus === "closed") {
        setResult(prev => {
          const newResult = { ...prev };
          const lastIndex = newResult.history.length - 1;
          if (lastIndex < 0) return newResult;
          newResult.history[lastIndex].isTextGenerating = false;
          return newResult;
        });
        if (!shouldFetchHistoryOnCloseRef.current) return;
        shouldFetchHistoryOnCloseRef.current = false;
        fetchHistoryList();
      }
    },
    onError: (error: unknown) => {
      const e = error as
        | {
            data: {
              details: {
                apiErrorCode: string;
                message: string;
              }[];
            };
          }
        | { message: string; status: number };

      // エラー時のハンドリング内容
      // https://docs.google.com/spreadsheets/d/1MNs7QLYV_0zdlFIClr6xG0BpzeQsm2sZfIU_G215CcA/edit?gid=1738421261#gid=1738421261
      let errorMessage = "";

      if ("status" in e) {
        const status = e.status;
        if (status === 504) {
          toast.setErrors(
            "処理がタイムアウトしました。しばらく時間をおいてから再度お試しください。"
          );
        } else if (status === 400 || status >= 500) {
          toast.setErrors(
            "システムエラーが発生しました。しばらく時間をおいてから再度お試しください。"
          );
        }
      } else if ("data" in e && e.data?.details?.[0]?.message) {
        errorMessage = e.data.details[0].message;
      }

      setResult(prev => {
        const newResult = { ...prev };
        const lastIndex = newResult.history.length - 1;
        if (lastIndex < 0) return newResult;
        newResult.history[lastIndex].isError = true;
        newResult.history[lastIndex].answer = errorMessage || "";
        newResult.history[lastIndex].isTextGenerating = false;
        return newResult;
      });
    }
  });

  // 質問を送信
  const sendQuestion = (text: string, overwrite = false) => {
    shouldFetchHistoryOnCloseRef.current = true;
    setResult(prev => {
      const initialDate = {
        question: text,
        answer: "",
        reference: [],
        questionAt: getNow("YYYY年MM月DD日 HH:mm"),
        questionOptimization: "",
        progress: "生成を開始します..."
      };

      if (overwrite) {
        return {
          ...prev,
          title: text,
          history: [...prev.history.slice(0, -1), initialDate]
        };
      } else {
        return {
          ...prev,
          title: prev.title || text,
          history: [...prev.history, initialDate]
        };
      }
    });

    trigger({
      talkId: result.talkId || "",
      question: text,
      history: JSON.stringify(
        result.history
          .filter(item => !item.isError) // エラー履歴は送信しない
          .slice(-5) // 直近5件の履歴を送信
          .map(chat => ({
            question: chat.question,
            answer: chat.answer
          }))
      )
    });
  };

  return { sseStatus: status, sendQuestion, close };
};
